import type { NormalizedNode } from "../parser"

/**
 * Serializes an array of nodes (useful for contentBlocks).
 */
export function serializeNodes(nodes: Array<NormalizedNode | string>): string {
	return nodes
		.map((node) => {
			if (typeof node === "string") {
				return escapeHtml(node)
			}
			return serializeNode(node)
		})
		.join("")
}

/**
 * Serializes the inner content of a node (its children) to an HTML string.
 * Preserves MathML and other XML content verbatim.
 */
export function serializeInner(node: NormalizedNode): string {
	return node.children
		.map((child: NormalizedNode | string) => {
			if (typeof child === "string") {
				return escapeHtml(child)
			}
			return serializeNode(child)
		})
		.join("")
}

/**
 * Serializes a complete node including its tag and attributes.
 * Critical for preserving MathML structure exactly as authored.
 */
export function serializeNode(node: NormalizedNode): string {
	// Self-closing tags
	const selfClosing = ["br", "hr", "img", "input", "meta", "link"]
	const tagName = stripQtiPrefix(node.tagName)

	// Preserve MathML elements verbatim without QTI processing
	if (tagName === "math" || isMathMLElement(tagName)) {
		return serializeVerbatim(node)
	}

	const attrs = serializeAttributes(node.attrs)
	const attrString = attrs ? ` ${attrs}` : ""

	if (selfClosing.includes(tagName) && node.children.length === 0) {
		return `<${tagName}${attrString} />`
	}

	const inner = serializeInner(node)
	return `<${tagName}${attrString}>${inner}</${tagName}>`
}

/**
 * Detects whether content contains MathML elements.
 * Used to determine appropriate rendering and sanitization strategies.
 */
export function detectContentType(html: string): "mathml" | "html" {
	// Quick heuristic: check for common MathML elements
	const mathmlIndicators = ["<math", "<mrow", "<msup", "<msub", "<mfrac", "<msqrt", "<mi>", "<mo>", "<mn>", "<mtext"]

	const normalized = html.toLowerCase()
	for (const indicator of mathmlIndicators) {
		if (normalized.includes(indicator)) {
			return "mathml"
		}
	}

	return "html"
}

/**
 * Strips QTI namespace prefixes from tag names.
 * Converts "qti-choice-interaction" to "div" or similar HTML equivalent.
 * Preserves MathML and standard HTML tags unchanged.
 */
function stripQtiPrefix(tagName: string): string {
	// Map QTI elements to semantic HTML equivalents
	const qtiToHtml: Record<string, string> = {
		"qti-item-body": "div",
		"qti-choice-interaction": "div",
		"qti-simple-choice": "div",
		"qti-feedback-block": "div",
		"qti-content-body": "div",
		"qti-prompt": "div",
		"qti-value": "span",
		"qti-variable": "span",
		"qti-correct-response": "div"
	}

	if (qtiToHtml[tagName]) {
		return qtiToHtml[tagName]
	}

	// Strip "qti-" prefix for unknown QTI elements
	if (tagName.startsWith("qti-")) {
		return tagName.slice(4)
	}

	// Preserve MathML and HTML tags as-is
	return tagName
}

/**
 * Serializes node attributes to an HTML attribute string.
 * Filters out QTI-specific attributes that shouldn't appear in rendered HTML.
 */
function serializeAttributes(attrs: Record<string, unknown>): string {
	// Attributes to exclude from HTML output
	const excludeAttrs = [
		"identifier",
		"response-identifier",
		"shuffle",
		"max-choices",
		"min-choices",
		"cardinality",
		"base-type",
		"outcome-identifier",
		"show-hide",
		"time-dependent",
		"adaptive",
		"xml-lang"
	]

	const parts: string[] = []

	for (const [key, value] of Object.entries(attrs)) {
		if (excludeAttrs.includes(key)) continue
		if (value == null) continue

		// Convert kebab-case QTI attrs to camelCase where appropriate
		const htmlKey = key === "xml:lang" ? "lang" : key
		const escapedValue = escapeHtml(String(value))
		parts.push(`${htmlKey}="${escapedValue}"`)
	}

	return parts.join(" ")
}

/**
 * Escapes HTML special characters to prevent XSS.
 * Used for text content, not for preserving markup.
 *
 * NOTE: We only escape < and > to prevent tag injection.
 * We preserve & because:
 * 1. The content goes through sanitizeHtml before display
 * 2. Entity references like &#xA0; must be preserved for proper rendering
 */
function escapeHtml(text: string): string {
	// Only escape angle brackets to prevent tag injection
	// Preserve & for entity references (&#xA0;, &nbsp;, etc.)
	return text.replace(/</g, "&lt;").replace(/>/g, "&gt;")
}

/**
 * Checks if a tag name is a MathML element.
 */
function isMathMLElement(tagName: string): boolean {
	const mathMLTags = [
		"mi",
		"mn",
		"mo",
		"mtext",
		"mspace",
		"ms",
		"mrow",
		"mfrac",
		"msqrt",
		"mroot",
		"mstyle",
		"msub",
		"msup",
		"msubsup",
		"munder",
		"mover",
		"munderover",
		"mtable",
		"mtr",
		"mtd"
	]
	return mathMLTags.includes(tagName)
}

/**
 * Serializes MathML nodes verbatim to preserve exact structure.
 * Does not apply QTI transformations or attribute filtering.
 */
function serializeVerbatim(node: NormalizedNode): string {
	const attrs = Object.entries(node.attrs)
		.map(([key, value]) => {
			if (value == null) return ""
			return ` ${key}="${String(value).replace(/"/g, "&quot;")}"`
		})
		.join("")

	if (node.children.length === 0) {
		return `<${node.tagName}${attrs} />`
	}

	const inner = node.children
		.map((child) => {
			if (typeof child === "string") {
				return escapeHtml(child)
			}
			// Recursively serialize MathML children verbatim
			return serializeVerbatim(child)
		})
		.join("")

	return `<${node.tagName}${attrs}>${inner}</${node.tagName}>`
}
